import { Ban, Download, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { messageLisible } from '@/shared/api/erreurs';
import {
  useDemandesRgpd,
  useEffacerDemandeRgpd,
  useRefuserDemandeRgpd,
  type DemandeRgpdDto,
} from '@/shared/api/hooksAdmin';
import { ACTES } from '@/shared/audit/actesSensibles';
import { DialogueActeSensible } from '@/shared/audit/DialogueActeSensible';
import { abrege, date } from '@/shared/lib/utils';
import { Bouton } from '@/shared/ui/Bouton';
import {
  Carte,
  EnTeteSection,
  Pastille,
  Table,
  type ColonneTable,
  type TonPastille,
} from '@/shared/ui/Table';

type ActeRgpd =
  | { type: 'effacer'; demande: DemandeRgpdDto }
  | { type: 'refuser'; demande: DemandeRgpdDto };

function ton(statut: string | null | undefined): TonPastille {
  switch (statut) {
    case 'Satisfaite':
      return 'succes';
    case 'SatisfaitePartiellement':
      return 'alerte';
    case 'Refusee':
      return 'danger';
    default:
      return 'accent';
  }
}

/**
 * Demandes RGPD escaladees a l'exploitant.
 *
 * QUI FAIT QUOI — la distinction est structurante et pas seulement documentaire :
 *
 * - **L'entreprise est responsable de traitement.** C'est elle qui repond aux demandes de
 *   ses propres clients, depuis son application. La grande majorite des demandes ne
 *   passent jamais par cet ecran.
 * - **L'exploitant est sous-traitant.** Il n'intervient que sur les demandes escaladees :
 *   entreprise disparue, litige, ou demande portant sur les donnees que la plateforme
 *   detient en propre.
 *
 * Consequence pratique : agir ici depuis cet ecran, c'est agir A LA PLACE du responsable
 * de traitement. Chaque acte exige donc un motif, et l'effacement une confirmation
 * renforcee — il est irreversible par construction, c'est meme son objet.
 */
export default function RgpdPage() {
  const { data, isPending, isError, error, refetch } = useDemandesRgpd();
  const [acte, setActe] = useState<ActeRgpd | null>(null);

  const effacer = useEffacerDemandeRgpd();
  const refuser = useRefuserDemandeRgpd();

  const colonnes: readonly ColonneTable<DemandeRgpdDto>[] = [
    {
      cle: 'type',
      entete: 'Demande',
      rendu: (ligne) => <span className="font-medium text-texte">{ligne.type}</span>,
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (ligne) => (
        <span className="flex items-center gap-1.5">
          <Pastille ton={ton(ligne.statut)}>{ligne.statut}</Pastille>
          {ligne.estEnRetard ? <Pastille ton="danger">En retard</Pastille> : null}
        </span>
      ),
    },
    {
      cle: 'entreprise',
      entete: 'Entreprise',
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-doux">{abrege(ligne.entrepriseId)}</span>,
    },
    {
      cle: 'recue',
      entete: 'Recue le',
      rendu: (ligne) => <span className="text-texte-doux">{date(ligne.recueLe)}</span>,
    },
    {
      cle: 'echeance',
      entete: 'Echeance',
      rendu: (ligne) => (
        <span className={ligne.estEnRetard ? 'text-danger' : 'text-texte-doux'}>
          {date(ligne.echeanceReponse)}
        </span>
      ),
    },
    {
      cle: 'actions',
      entete: '',
      rendu: (ligne) => {
        const close = ligne.statut === 'Satisfaite' || ligne.statut === 'Refusee';

        return (
          <div className="flex justify-end gap-1">
            {close ? (
              <span className="text-texte-faible">Close</span>
            ) : (
              <>
                {ligne.type === 'Effacement' ? (
                  <Bouton
                    variante="discret"
                    taille="sm"
                    onClick={() => setActe({ type: 'effacer', demande: ligne })}
                  >
                    <Trash2 />
                    Effacer
                  </Bouton>
                ) : null}

                <Bouton
                  variante="discret"
                  taille="sm"
                  onClick={() => setActe({ type: 'refuser', demande: ligne })}
                >
                  <Ban />
                  Refuser
                </Bouton>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <EnTeteSection
        titre="RGPD"
        description="Demandes escaladees a l’exploitant."
        sensible="Agir ici, c’est agir a la place du responsable de traitement. Chaque acte exige un motif et reste consigne."
      />

      <Carte className="p-3">
        <p className="text-texte-doux">
          <strong className="text-texte">Repartition des roles.</strong> L’entreprise est
          responsable de traitement : elle repond elle-meme aux demandes de ses clients, depuis
          son application. L’exploitant est sous-traitant et n’intervient que sur les demandes
          escaladees — entreprise disparue, litige, ou donnees detenues par la plateforme.
        </p>
      </Carte>

      <Table
        colonnes={colonnes}
        lignes={data}
        cleLigne={(ligne) => ligne.demandeId ?? ''}
        chargement={isPending}
        erreur={isError ? error : undefined}
        onReessayer={() => void refetch()}
        messageVide="Aucune demande escaladee."
      />

      {acte?.type === 'effacer' ? (
        <DialogueActeSensible
          acte={ACTES.rgpdEffacementExecute}
          cible={`Demande ${acte.demande.type} — ${abrege(acte.demande.demandeId)}`}
          enCours={effacer.isPending}
          onFermer={() => setActe(null)}
          onConfirmer={() =>
            effacer.mutate(acte.demande.demandeId as string, {
              onSuccess: () => {
                toast.success('Effacement execute', {
                  description: 'Rapport d’anonymisation produit, acte consigne.',
                });
                setActe(null);
              },
              onError: (erreur) =>
                toast.error('Effacement impossible', { description: messageLisible(erreur) }),
            })
          }
        />
      ) : null}

      {acte?.type === 'refuser' ? (
        <DialogueActeSensible
          acte={ACTES.rgpdDemandeRefusee}
          cible={`Demande ${acte.demande.type} — ${abrege(acte.demande.demandeId)}`}
          enCours={refuser.isPending}
          onFermer={() => setActe(null)}
          onConfirmer={(motif) =>
            refuser.mutate(
              { demandeId: acte.demande.demandeId as string, motif },
              {
                onSuccess: () => {
                  toast.success('Demande refusee', { description: 'Le motif est opposable.' });
                  setActe(null);
                },
                onError: (erreur) =>
                  toast.error('Refus impossible', { description: messageLisible(erreur) }),
              },
            )
          }
        />
      ) : null}

      <Bouton asChild variante="discret" taille="sm">
        <a href="/journal">
          <Download />
          Voir ces actes dans le journal d’audit
        </a>
      </Bouton>
    </div>
  );
}
